import {
    ApiSet,
    BaseResponse,
    IndexApiSet,
    useDeleteApi,
    useShowApi,
    useIndexApi,
    usePostApi,
    usePutApi,
    usePatchApi,
    useDownloadApi,
} from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { Task, TaskForm, TaskSearchForm } from 'entities/task'

type TasksResponse = PagingResponse & {
    results: Task[]
}

export function useFetchTasksApi(searchForm?: Form<TaskSearchForm>): IndexApiSet<TasksResponse> & { execute: () => void } {
    const apiPath = 'tasks/'
    const api = useIndexApi<TasksResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchTaskApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `tasks/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostTaskApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskForm>) => void } {
    const apiSet = usePostApi<BaseResponse, TaskForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<TaskForm>) => {
        const apiPath = `tasks/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutTaskApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskForm>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<TaskForm>) => {
        const apiPath = `tasks/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutTasksApi(): ApiSet<BaseResponse> & { execute: (form: Form<TaskForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, TaskForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<TaskForm[]>) => {
        const apiPath = `tasks/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteTaskApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `tasks/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useCompleteTaskApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = usePatchApi<BaseResponse, TaskForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `tasks/${id}/complete/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function useCancelTaskApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = usePatchApi<BaseResponse, TaskForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `tasks/${id}/cancel/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export type YamatoDownloadForm = {
    kind?: string
    postDate?: string
    deliverDate?: string
    time?: string
    taskGroupId?: string
    location?: string
    fastest?: boolean
    homeProductId?: number
}

export function useDownloadYamatoApi(): ApiSet<BaseResponse> & { execute: (form: Form<YamatoDownloadForm>) => void } {
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<YamatoDownloadForm>): void => {
        const apiPath = `tasks/materials/export/`
        api.execute(apiPath, `newb2web_template1.csv`, form.object)
    }

    return { ...api, execute: execute }
}
